// frontend/src/pages/UnidadesLocaveis.js
import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import "./CadastroImoveis.css";

import ThemeProvider from "../theme/TemaMuiProvider";
import {
  Box,
  Button,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import { FaCopy, FaEye, FaTrash, FaEdit, FaSave, FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";

import muiTheme from "../theme/muiTheme";

/* =======================
   MÁSCARA DE METRAGEM (4 casas decimais)
   ======================= */
const METRAGEM_DECIMAIS = 4;

function onlyDigitsAndComma(value = "") {
  let v = String(value)
    .replace(/\./g, "")
    .replace(/\s/g, "")
    .replace(/[^\d,]/g, "");

  const firstComma = v.indexOf(",");
  if (firstComma !== -1) {
    v = v.slice(0, firstComma + 1) + v.slice(firstComma + 1).replace(/,/g, "");
  }
  return v;
}

function addThousandDots(intPart = "") {
  if (!intPart) return "";
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function formatMetragemTyping(raw = "") {
  const v = onlyDigitsAndComma(raw);

  const hasComma = v.includes(",");
  const [intRaw, decRaw = ""] = v.split(",");

  const intDigits = intRaw.replace(/\D/g, "");
  const decDigits = decRaw.replace(/\D/g, "").slice(0, METRAGEM_DECIMAIS);

  const intFormatted = addThousandDots(intDigits);

  if (hasComma) return `${intFormatted},${decDigits}`;
  return intFormatted;
}

function finalizeMetragem(raw = "") {
  const v = onlyDigitsAndComma(raw);
  if (!v) return "";

  const hasComma = v.includes(",");
  let [intRaw, decRaw = ""] = v.split(",");

  const intDigits = intRaw.replace(/\D/g, "");
  const intFormatted = addThousandDots(intDigits);

  let decDigits = decRaw.replace(/\D/g, "").slice(0, METRAGEM_DECIMAIS);

  if (!hasComma) {
    return `${intFormatted},${"0".repeat(METRAGEM_DECIMAIS)}`;
  }

  decDigits = decDigits.padEnd(METRAGEM_DECIMAIS, "0");
  return `${intFormatted},${decDigits}`;
}

/* =======================
   NORMALIZA PARA EXIBIÇÃO (VISUALIZAR)
   - garante 4 casas
   ======================= */
function normalizeToPtBr(value) {
  if (value === null || value === undefined) return "";
  const s = String(value).trim();
  if (!s) return "";

  if (s.includes(".") && !s.includes(",")) {
    return finalizeMetragem(s.replace(".", ","));
  }

  return finalizeMetragem(s);
}

const muiCampoSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 56,
    display: "flex",
    alignItems: "center",

    "& fieldset": {
      borderColor: "var(--ig-b-555555)",
    },

    "&:hover fieldset": {
      borderColor: "var(--ig-b-777777)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#2ea8ff",
      borderWidth: 2,
    },

    "&.Mui-error fieldset": {
      borderColor: "#ef4444 !important",
      borderWidth: 2,
    },

    "&.Mui-disabled fieldset": {
      borderColor: "var(--ig-b-555555)",
    },
  },

  "& .MuiOutlinedInput-input": {
    padding: "16.5px 14px",
    color: "var(--ig-t-ffffff)",
  },

  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "var(--ig-t-ffffff)",
    color: "var(--ig-t-ffffff)",
  },

  "& .MuiInputLabel-root": {
    color: "var(--ig-t-ffffff)",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#2ea8ff",
  },

  "& .MuiInputLabel-root.Mui-error": {
    color: "#ef4444 !important",
  },

  "& .MuiInputLabel-root.Mui-disabled": {
    color: "var(--ig-t-ffffff)",
  },

  "& .MuiSvgIcon-root": {
    color: "var(--ig-t-ffffff)",
  },
};

function normalizarGeometriaRegular(valor) {
  if (valor === true || valor === 1 || valor === "1") {
    return "Sim";
  }

  const texto = String(valor ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  return texto === "sim" || texto === "true" || texto === "s" ? "Sim" : "Não";
}

function UnidadesLocaveis() {
  const [unidades, setUnidades] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [novaUnidade, setNovaUnidade] = useState("");
  const [editandoId, setEditandoId] = useState(null);
  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [visualizandoId, setVisualizandoId] = useState(null);

  const [imoveisBuscaLista, setImoveisBuscaLista] = useState([]);
  const [modalSelecionarImovel, setModalSelecionarImovel] = useState(false);
  const [filtroImovel, setFiltroImovel] = useState("");
  const [paginaImovel, setPaginaImovel] = useState(0);
  const [linhasPorPaginaImovel, setLinhasPorPaginaImovel] = useState(10);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [unidadesPorPagina, setUnidadesPorPagina] = useState(5);

  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("unidadesLocaveisGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("unidadesLocaveisGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  const [complemento, setComplemento] = useState("");
  const [lote, setLote] = useState("");
  const [metrosFrente, setMetrosFrente] = useState("");
  const [metrosFundo, setMetrosFundo] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [geometriaRegular, setGeometriaRegular] = useState("Não");
  const [metrosLadoDireito, setMetrosLadoDireito] = useState("");
  const [metrosLadoEsquerdo, setMetrosLadoEsquerdo] = useState("");

  const [dadosMatricula, setDadosMatricula] = useState({
    matriculaId: null,
    codigoMatricula: "",
    apelido: "",
    nomeProprietario: "",
    nomeLocatario: "",
  });

  const [mostrarPendencias, setMostrarPendencias] = useState(false);

  const handleSelecionarMatricula = (m) => {
    setDadosMatricula({
      matriculaId: m?.id ?? null,
      codigoMatricula: m?.codigoMatricula ?? "",
      apelido: m?.apelido ?? "",
      nomeProprietario: m?.nomeProprietario ?? "",
      nomeLocatario: m?.nomeLocatario ?? "",
    });
  };

  const limparMatricula = () => {
    setDadosMatricula({
      matriculaId: null,
      codigoMatricula: "",
      apelido: "",
      nomeProprietario: "",
      nomeLocatario: "",
    });
  };

  const limparFormulario = () => {
    setNovaUnidade("");
    setComplemento("");
    setLote("");
    setMetrosFrente("");
    setMetrosFundo("");
    setObservacoes("");
    setGeometriaRegular("Não");
    setMetrosLadoDireito("");
    setMetrosLadoEsquerdo("");
    setEditandoId(null);
    setModoVisualizacao(false);
    setVisualizandoId(null);
    limparMatricula();
    setModalSelecionarImovel(false);
    setFiltroImovel("");
    setPaginaImovel(0);
    setMostrarPendencias(false);
  };

  const camposOpcionais = ["complemento", "lote", "metrosFrente", "metrosFundo", "metrosLadoDireito", "metrosLadoEsquerdo", "observacoes", "matriculaId"];

  const estaVazio = (v) => !String(v ?? "").trim();

  const classeCampoOpcional = (campo) => {
    if (!mostrarPendencias) return "";

    if (campo === "matriculaId") {
      return !dadosMatricula.matriculaId ? "campo-pendente" : "";
    }

    const mapValores = {
      complemento,
      lote,
      metrosFrente,
      metrosFundo,
      metrosLadoDireito,
      metrosLadoEsquerdo,
      observacoes,
    };

    return camposOpcionais.includes(campo) && estaVazio(mapValores[campo]) ? "campo-pendente" : "";
  };

  const erroCampoMUI = (campo) => {
    if (!mostrarPendencias) return false;

    if (campo === "matriculaId") {
      return !dadosMatricula.matriculaId;
    }

    const valores = {
      complemento,
      lote,
      metrosFrente,
      metrosFundo,
      metrosLadoDireito,
      metrosLadoEsquerdo,
      observacoes,
    };

    return estaVazio(valores[campo]);
  };

  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    const decoded = jwtDecode(token);
    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  const isReadOnly = modoVisualizacao === true;

  const handleMetragemChange = (campo) => (e) => {
    const v = formatMetragemTyping(e.target.value);

    if (campo === "metrosFrente") setMetrosFrente(v);
    if (campo === "metrosFundo") setMetrosFundo(v);
    if (campo === "metrosLadoDireito") setMetrosLadoDireito(v);
    if (campo === "metrosLadoEsquerdo") setMetrosLadoEsquerdo(v);
  };

  const handleMetragemBlur = (campo) => () => {
    if (campo === "metrosFrente") setMetrosFrente(finalizeMetragem(metrosFrente));
    if (campo === "metrosFundo") setMetrosFundo(finalizeMetragem(metrosFundo));
    if (campo === "metrosLadoDireito") setMetrosLadoDireito(finalizeMetragem(metrosLadoDireito));
    if (campo === "metrosLadoEsquerdo") setMetrosLadoEsquerdo(finalizeMetragem(metrosLadoEsquerdo));
  };

  useEffect(() => {
    if (!podeVisualizar) return;

    const tk = localStorage.getItem("token");

    fetch("/api/unidades-locaveis", {
      headers: {
        Authorization: `Bearer ${tk}`,
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.erro || "Não foi possível carregar as unidades locáveis.");
        }

        return data;
      })
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data?.unidades) ? data.unidades : [];

        setUnidades(lista);
      })
      .catch((err) => {
        console.error("Erro ao buscar unidades locáveis:", err);

        setUnidades([]);

        alert(err.message || "Erro ao carregar unidades locáveis.");
      });
  }, [podeVisualizar]);

  useEffect(() => {
    if (!podeVisualizar) return;

    const tk = localStorage.getItem("token");

    fetch("/api/imoveis", {
      headers: {
        Authorization: `Bearer ${tk}`,
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => []);

        if (!res.ok) {
          throw new Error(data?.erro || "Não foi possível carregar os imóveis cadastrados.");
        }

        return data;
      })
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data?.imoveis) ? data.imoveis : [];

        setImoveisBuscaLista(lista);
      })
      .catch((err) => {
        console.error("Erro ao carregar imóveis:", err);
        setImoveisBuscaLista([]);
      });
  }, [podeVisualizar]);

  const imoveisFiltradosBusca = imoveisBuscaLista.filter((imovel) => {
    const termo = filtroImovel.trim().toLowerCase();

    if (!termo) return true;

    const texto = [
      imovel?.codigo,
      imovel?.codigoMatricula,
      imovel?.apelido,
      imovel?.endereco,
      imovel?.bairro,
      imovel?.cidade,
      imovel?.estado,
      imovel?.uf,
      imovel?.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return texto.includes(termo);
  });

  const imoveisPaginadosBusca = imoveisFiltradosBusca.slice(paginaImovel * linhasPorPaginaImovel, paginaImovel * linhasPorPaginaImovel + linhasPorPaginaImovel);

  const fecharModalSelecionarImovel = () => {
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }

    setTimeout(() => {
      setModalSelecionarImovel(false);
      setFiltroImovel("");
      setPaginaImovel(0);
    }, 0);
  };

  const selecionarImovel = (imovel) => {
    if (!imovel) return;

    setDadosMatricula({
      matriculaId: imovel.id ?? imovel.matriculaId ?? null,
      codigoMatricula: imovel.codigoMatricula ?? imovel.codigo ?? "",
      apelido: imovel.apelido ?? "",
      nomeProprietario: imovel.nomeProprietario ?? imovel.proprietario ?? "",
      nomeLocatario: imovel.nomeLocatario ?? imovel.locatario ?? "",
    });

    fecharModalSelecionarImovel();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const botaoSubmit = e.nativeEvent?.submitter;

    if (!botaoSubmit || botaoSubmit.type !== "submit") {
      return;
    }

    setMostrarPendencias(true);

    const mfFinal = finalizeMetragem(metrosFrente);
    const mbFinal = finalizeMetragem(metrosFundo);
    const mldFinal = finalizeMetragem(metrosLadoDireito);
    const mleFinal = finalizeMetragem(metrosLadoEsquerdo);

    if (!dadosMatricula.matriculaId) {
      alert(editandoId ? "Selecione uma matrícula antes de atualizar." : "Selecione uma matrícula antes de cadastrar.");
      return;
    }

    if (!novaUnidade.trim()) {
      alert("Informe o nome da unidade locável.");
      return;
    }

    if (!metrosFrente.trim()) {
      alert("Informe os metros de frente.");
      return;
    }

    const tk = localStorage.getItem("token");

    if (editandoId) {
      if (!podeEditar) {
        alert("Você não tem permissão para atualizar.");
        return;
      }

      fetch(`/api/unidades-locaveis/${editandoId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tk}`,
        },
        body: JSON.stringify({
          nome: novaUnidade,
          matriculaId: dadosMatricula.matriculaId,
          complemento,
          lote,

          metrosFrente: mfFinal,
          metros_frente: mfFinal,

          metrosFundo: geometriaRegular === "Sim" ? mbFinal : "",
          metros_fundo: geometriaRegular === "Sim" ? mbFinal : "",

          observacoes,

          geometriaRegular,
          geometria_regular: geometriaRegular,

          metrosLadoDireito: geometriaRegular === "Sim" ? mldFinal : "",
          metros_lado_direito: geometriaRegular === "Sim" ? mldFinal : "",

          metrosLadoEsquerdo: geometriaRegular === "Sim" ? mleFinal : "",
          metros_lado_esquerdo: geometriaRegular === "Sim" ? mleFinal : "",
        }),
      })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));

          if (!res.ok) {
            throw new Error(data?.erro || "Não foi possível atualizar a unidade locável.");
          }

          if (!data?.id) {
            throw new Error("O backend não retornou o registro atualizado.");
          }

          setUnidades((prev) => prev.map((unidade) => (unidade.id === editandoId ? data : unidade)));

          limparFormulario();

          alert(data?.mensagem || "Unidade locável atualizada com sucesso!");
        })
        .catch((err) => {
          console.error("Erro ao atualizar unidade locável:", err);

          alert(err.message || "Erro ao atualizar unidade locável.");
        });

      return;
    }

    if (!podeCadastrar) {
      alert("Você não tem permissão para cadastrar unidades locáveis.");
      return;
    }

    const nomeFormatado = novaUnidade.trim().toLowerCase();
    if (unidades.some((u) => (u.nome ?? "").trim().toLowerCase() === nomeFormatado)) {
      alert("Unidade já cadastrada.");
      return;
    }

    fetch("/api/unidades-locaveis", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tk}`,
      },
      body: JSON.stringify({
        nome: novaUnidade,
        matriculaId: dadosMatricula.matriculaId,
        complemento,
        lote,

        metrosFrente: mfFinal,
        metros_frente: mfFinal,

        metrosFundo: geometriaRegular === "Sim" ? mbFinal : "",
        metros_fundo: geometriaRegular === "Sim" ? mbFinal : "",

        observacoes,

        geometriaRegular,
        geometria_regular: geometriaRegular,

        metrosLadoDireito: geometriaRegular === "Sim" ? mldFinal : "",
        metros_lado_direito: geometriaRegular === "Sim" ? mldFinal : "",

        metrosLadoEsquerdo: geometriaRegular === "Sim" ? mleFinal : "",
        metros_lado_esquerdo: geometriaRegular === "Sim" ? mleFinal : "",
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.erro || "Não foi possível cadastrar a unidade locável.");
        }

        if (!data?.id) {
          throw new Error("O backend não retornou o registro cadastrado.");
        }

        setUnidades((prev) => [data, ...prev]);
        setPaginaAtual(1);
        limparFormulario();

        alert(data?.mensagem || "Unidade locável cadastrada com sucesso!");
      })
      .catch((err) => {
        console.error("Erro ao cadastrar unidade locável:", err);

        alert(err.message || "Erro ao cadastrar unidade locável.");
      });
  };

  const handleVisualizar = (unidade) => {
    setVisualizandoId(unidade.id);
    setModoVisualizacao(true);
    setEditandoId(null);

    setNovaUnidade(unidade.nome ?? "");

    setComplemento(unidade.complemento ?? "");
    setLote(unidade.lote ?? "");

    setMetrosFrente(normalizeToPtBr(unidade?.metrosFrente ?? unidade?.metros_frente));

    setMetrosFundo(normalizeToPtBr(unidade?.metrosFundo ?? unidade?.metros_fundo));

    setObservacoes(unidade.observacoes ?? "");

    setGeometriaRegular(normalizarGeometriaRegular(unidade?.geometriaRegular ?? unidade?.geometria_regular));

    setMetrosLadoDireito(normalizeToPtBr(unidade?.metrosLadoDireito ?? unidade?.metros_lado_direito));

    setMetrosLadoEsquerdo(normalizeToPtBr(unidade?.metrosLadoEsquerdo ?? unidade?.metros_lado_esquerdo));

    setDadosMatricula({
      matriculaId: unidade?.matriculaId ?? null,
      codigoMatricula: unidade?.codigoMatricula ?? "",
      apelido: unidade?.apelido ?? "",
      nomeProprietario: unidade?.nomeProprietario ?? "",
      nomeLocatario: unidade?.nomeLocatario ?? "",
    });

    setMostrarPendencias(true);

    const tk = localStorage.getItem("token");
    fetch(`/api/unidades-locaveis/${unidade.id}`, {
      headers: {
        Authorization: `Bearer ${tk}`,
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.erro || "Não foi possível carregar os detalhes da unidade locável.");
        }

        return data;
      })
      .then((data) => {
        if (!data) return;

        setNovaUnidade(data?.nome ?? unidade?.nome ?? "");

        setComplemento(data?.complemento ?? unidade?.complemento ?? "");

        setLote(data?.lote ?? unidade?.lote ?? "");

        setMetrosFrente(normalizeToPtBr(data?.metrosFrente ?? data?.metros_frente ?? unidade?.metrosFrente ?? unidade?.metros_frente));

        setMetrosFundo(normalizeToPtBr(data?.metrosFundo ?? data?.metros_fundo ?? unidade?.metrosFundo ?? unidade?.metros_fundo));

        setObservacoes(data?.observacoes ?? unidade?.observacoes ?? "");

        setGeometriaRegular(
          normalizarGeometriaRegular(data?.geometriaRegular ?? data?.geometria_regular ?? unidade?.geometriaRegular ?? unidade?.geometria_regular),
        );

        setMetrosLadoDireito(
          normalizeToPtBr(data?.metrosLadoDireito ?? data?.metros_lado_direito ?? unidade?.metrosLadoDireito ?? unidade?.metros_lado_direito),
        );

        setMetrosLadoEsquerdo(
          normalizeToPtBr(data?.metrosLadoEsquerdo ?? data?.metros_lado_esquerdo ?? unidade?.metrosLadoEsquerdo ?? unidade?.metros_lado_esquerdo),
        );

        setDadosMatricula({
          matriculaId: data?.matriculaId ?? unidade?.matriculaId ?? null,
          codigoMatricula: data?.codigoMatricula ?? unidade?.codigoMatricula ?? "",
          apelido: data?.apelido ?? unidade?.apelido ?? "",
          nomeProprietario: data?.nomeProprietario ?? unidade?.nomeProprietario ?? "",
          nomeLocatario: data?.nomeLocatario ?? unidade?.nomeLocatario ?? "",
        });
      })
      .catch((err) => {
        console.error("Erro ao buscar detalhes da unidade locável:", err);

        alert(err.message || "Erro ao buscar detalhes da unidade locável.");
      });

    setTimeout(() => {
      document.querySelector(".bloco-formulario")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleEditar = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!podeEditar || !visualizandoId) return;

    setEditandoId(visualizandoId);
    setModoVisualizacao(false);

    setTimeout(() => {
      document.querySelector(".bloco-formulario")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleDelete = (id) => {
    if (!podeExcluir) {
      alert("Você não tem permissão para excluir.");
      return;
    }

    const confirmar = window.confirm("Deseja realmente excluir esta unidade?");
    if (!confirmar) return;

    const tk = localStorage.getItem("token");
    fetch(`/api/unidades-locaveis/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.erro || "Não foi possível excluir a unidade locável.");
        }

        setUnidades((prev) => prev.filter((unidade) => unidade.id !== id));

        limparFormulario();

        alert(data?.mensagem || "Unidade locável excluída com sucesso!");
      })
      .catch((err) => {
        console.error("Erro ao excluir unidade locável:", err);

        alert(err.message || "Erro ao excluir unidade locável.");
      });
  };

  const unidadesFiltradas = unidades.filter((unidade) => {
    const termo = filtro.trim().toLowerCase();

    if (!termo) return true;

    return [
      unidade?.nome,
      unidade?.codigoMatricula,
      unidade?.apelido,
      unidade?.complemento,
      unidade?.lote,
      unidade?.nomeProprietario,
      unidade?.nomeLocatario,
    ].some((valor) =>
      String(valor ?? "")
        .toLowerCase()
        .includes(termo),
    );
  });

  const totalRegistros = unidadesFiltradas.length;

  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / unidadesPorPagina));

  const indexInicial = (paginaAtual - 1) * unidadesPorPagina;

  const indexFinal = Math.min(indexInicial + unidadesPorPagina, totalRegistros);

  const unidadesPaginadas = unidadesFiltradas.slice(indexInicial, indexFinal);

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(1);
  }, [paginaAtual, totalPaginas]);

  const handleCopiar = async () => {
    const texto = (unidadesFiltradas || [])
      .map((u) => u?.nome ?? "")
      .filter(Boolean)
      .join(", ");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);
    alert(ok ? "Unidades copiadas!" : "Não foi possível copiar automaticamente.");
  };

  const handleCSV = () => {
    const BOM = "\uFEFF";

    const header = [
      "Unidade Locável",
      "Código da Matrícula",
      "Apelido do Imóvel",
      "Proprietário",
      "Locatário",
      "Complemento",
      "Lote",
      "Geometria Regular",
      "Metros de Frente (m²)",
      "Metros de Fundo (m²)",
      "Metros Lado Direito (m²)",
      "Metros Lado Esquerdo (m²)",
      "Observações",
    ];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const formatarMetragemCSV = (value) => {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      return normalizeToPtBr(value);
    };

    const linhas = (unidadesFiltradas || []).map((unidade) => {
      const geometria = normalizarGeometriaRegular(unidade?.geometriaRegular ?? unidade?.geometria_regular);

      return [
        unidade?.nome ?? "",
        unidade?.codigoMatricula ?? "",
        unidade?.apelido ?? "",
        unidade?.nomeProprietario ?? "",
        unidade?.nomeLocatario ?? "",
        unidade?.complemento ?? "",
        unidade?.lote ?? "",
        geometria,
        formatarMetragemCSV(unidade?.metrosFrente ?? unidade?.metros_frente),
        formatarMetragemCSV(unidade?.metrosFundo ?? unidade?.metros_fundo),
        formatarMetragemCSV(unidade?.metrosLadoDireito ?? unidade?.metros_lado_direito),
        formatarMetragemCSV(unidade?.metrosLadoEsquerdo ?? unidade?.metros_lado_esquerdo),
        unidade?.observacoes ?? "",
      ].map(csvEscape);
    });

    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", "unidades-locaveis.csv");

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  if (!podeVisualizar) {
    return (
      <ThemeProvider theme={muiTheme}>
        <div className="local-container">
          <h2>Unidades Locáveis</h2>

          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você não tem permissão para visualizar esta página.</p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        <h2>Unidades Locáveis</h2>

        <Box
          className="bloco-linha"
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              height: 52,
              width: "100%",
              border: "1px solid var(--ig-b-555555)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              px: 2,
            }}
          >
            <input
              type="text"
              placeholder="Filtrar Unidades Locáveis"
              value={filtro}
              onChange={(e) => {
                setFiltro(e.target.value);
                setPaginaAtual(1);
              }}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ig-t-ffffff)",
                fontSize: "1rem",
              }}
            />

            <SearchIcon
              sx={{
                color: "var(--ig-t-9ca3af)",
                fontSize: 20,
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => {
                setFiltro("");
                setPaginaAtual(1);
              }}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCopiar}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <FaCopy style={{ marginRight: 6 }} />
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>
        </Box>

        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
            >
              <FaChevronDown />
              Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
            >
              <FaChevronUp />
              Expandir
            </button>
          )}
        </div>

        {!gridMinimizado && (
          <div className="bloco-lista">
            {unidadesFiltradas.length === 0 ? (
              <p>Não há registros para exibir</p>
            ) : (
              <>
                <table className="tabela-matriculas">
                  <colgroup>
                    <col style={{ width: "35%" }} />
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "30%" }} />
                    <col style={{ width: "25%" }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th>Unidade Locável</th>
                      <th>Matrícula do Imóvel</th>
                      <th>Complemento</th>
                      <th>Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {unidadesPaginadas.map((unidade) => (
                      <tr key={unidade.id}>
                        <td>{unidade.nome || "—"}</td>

                        <td>{unidade.codigoMatricula ? `${unidade.codigoMatricula} - ${unidade.apelido || ""}` : "—"}</td>

                        <td>{unidade.complemento || "—"}</td>

                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "12px",
                              flexWrap: "nowrap",
                              width: "100%",
                            }}
                          >
                            {podeVisualizar && (
                              <button
                                className="btn-editar"
                                type="button"
                                onClick={() => handleVisualizar(unidade)}
                                style={{
                                  width: "100px",
                                  minWidth: "100px",
                                  height: "44px",
                                  minHeight: "44px",
                                  maxHeight: "44px",
                                  padding: "0 12px",
                                  boxSizing: "border-box",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                  whiteSpace: "nowrap",
                                  lineHeight: "1",
                                  margin: 0,
                                }}
                              >
                                <FaEye />
                                Visualizar
                              </button>
                            )}

                            {podeExcluir && (
                              <button
                                className="btn-excluir"
                                type="button"
                                onClick={() => handleDelete(unidade.id)}
                                style={{
                                  width: "100px",
                                  minWidth: "100px",
                                  height: "44px",
                                  minHeight: "44px",
                                  maxHeight: "44px",
                                  padding: "0 12px",
                                  boxSizing: "border-box",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                  whiteSpace: "nowrap",
                                  lineHeight: "1",
                                  margin: 0,
                                }}
                              >
                                <FaTrash />
                                Excluir
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  component="div"
                  count={unidadesFiltradas.length}
                  page={paginaAtual - 1}
                  onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                  rowsPerPage={unidadesPorPagina}
                  onRowsPerPageChange={(e) => {
                    setUnidadesPorPagina(parseInt(e.target.value, 10));
                    setPaginaAtual(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="Unidades por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  sx={{
                    fontSize: "0.8rem",
                    minHeight: 42,
                    color: "var(--ig-t-ffffff)",
                    borderTop: "1px solid var(--ig-b-444444)",

                    "& .MuiTablePagination-toolbar": {
                      minHeight: 42,
                    },

                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "0.8rem",
                    },

                    "& .MuiTablePagination-selectIcon": {
                      color: "var(--ig-t-ffffff)",
                    },

                    "& .MuiTablePagination-actions button": {
                      color: "var(--ig-t-ffffff)",
                      width: "auto",
                    },

                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </>
            )}
          </div>
        )}

        <Dialog
          open={modalSelecionarImovel}
          onClose={fecharModalSelecionarImovel}
          maxWidth="md"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                bgcolor: "var(--ig-f-2c2c2c)",
                color: "var(--ig-t-ffffff)",
                backgroundImage: "none",
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
            }}
          >
            Selecionar Imóvel
            <IconButton type="button" onClick={fecharModalSelecionarImovel}>
              <CloseIcon sx={{ color: "var(--ig-t-aaaaaa)" }} />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mb: 2,
              }}
            >
              <TextField
                value={filtroImovel}
                onChange={(e) => {
                  setFiltroImovel(e.target.value);
                  setPaginaImovel(0);
                }}
                placeholder="Código, apelido, endereço, bairro ou cidade..."
                fullWidth
                size="small"
                sx={muiCampoSx}
              />

              <Button
                type="button"
                variant="contained"
                sx={{
                  minWidth: 110,
                }}
              >
                Pesquisar
              </Button>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Apelido</TableCell>
                  <TableCell>Endereço</TableCell>
                  <TableCell>Bairro</TableCell>
                  <TableCell>Cidade/UF</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {imoveisPaginadosBusca.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: "var(--ig-t-ffffff)" }}>
                      Nenhum imóvel encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  imoveisPaginadosBusca.map((imovel) => (
                    <TableRow
                      key={imovel.id || imovel.codigo || imovel.codigoMatricula || imovel.apelido}
                      hover
                      onClick={() => selecionarImovel(imovel)}
                      sx={{
                        cursor: "pointer",
                      }}
                    >
                      <TableCell>{imovel.codigo || imovel.codigoMatricula || ""}</TableCell>

                      <TableCell sx={{ fontWeight: 700 }}>{imovel.apelido || ""}</TableCell>

                      <TableCell>{imovel.endereco || ""}</TableCell>

                      <TableCell>{imovel.bairro || ""}</TableCell>

                      <TableCell>{[imovel.cidade, imovel.estado || imovel.uf].filter(Boolean).join("/")}</TableCell>

                      <TableCell>{imovel.status || ""}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={imoveisFiltradosBusca.length}
              page={paginaImovel}
              onPageChange={(_, novaPagina) => setPaginaImovel(novaPagina)}
              rowsPerPage={linhasPorPaginaImovel}
              onRowsPerPageChange={(e) => {
                setLinhasPorPaginaImovel(parseInt(e.target.value, 10));
                setPaginaImovel(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "var(--ig-t-ffffff)",

                "& .MuiTablePagination-selectIcon": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-actions button": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            />
          </DialogContent>
        </Dialog>

        {podeCadastrar || (podeVisualizar && modoVisualizacao) || (podeEditar && editandoId) ? (
          <div className="bloco-formulario">
            <h3
              style={{
                width: "100%",
                margin: "0 0 20px 0",
                color: "var(--ig-t-ffffff)",
                fontSize: "1.25rem",
                fontWeight: 700,
              }}
            >
              UNIDADE LOCÁVEL
            </h3>

            <form
              className="form-grid"
              onSubmit={(e) => {
                if (modoVisualizacao) {
                  e.preventDefault();
                  return;
                }

                handleSubmit(e);
              }}
            >
              {/* 1. Matrícula do Imóvel / 2. Nome da Unidade Locável */}
              <div
                className="form-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(320px, 0.8fr) minmax(0, 1.2fr)",
                  gap: "16px",
                  alignItems: "start",
                }}
              >
                <TextField
                  label="Matrícula do Imóvel*"
                  value={
                    dadosMatricula.codigoMatricula ? `${dadosMatricula.codigoMatricula}${dadosMatricula.apelido ? ` - ${dadosMatricula.apelido}` : ""}` : ""
                  }
                  onClick={() => {
                    if (isReadOnly) return;

                    setFiltroImovel("");
                    setPaginaImovel(0);
                    setModalSelecionarImovel(true);
                  }}
                  disabled={isReadOnly}
                  error={erroCampoMUI("matriculaId")}
                  required
                  fullWidth
                  size="small"
                  placeholder="Clique para selecionar o imóvel"
                  sx={{
                    ...muiCampoSx,
                    cursor: isReadOnly ? "default" : "pointer",

                    "& .MuiInputBase-input": {
                      cursor: isReadOnly ? "default" : "pointer",
                      color: "var(--ig-t-ffffff)",
                    },

                    "& .MuiInputAdornment-root": {
                      marginLeft: "8px",
                    },
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <SearchIcon
                        sx={{
                          color: "var(--ig-t-ffffff)",
                          fontSize: 22,
                        }}
                      />
                    ),
                  }}
                />

                <TextField
                  label="Nome Unidade Locável*"
                  value={novaUnidade}
                  onChange={(e) => setNovaUnidade(e.target.value.toUpperCase())}
                  disabled={isReadOnly}
                  required
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              {/* 3. Complemento / 4. Lote */}
              <div className="form-row">
                <TextField
                  label="Complemento"
                  value={complemento}
                  onChange={(e) => setComplemento(e.target.value.toUpperCase())}
                  disabled={isReadOnly}
                  error={erroCampoMUI("complemento")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  label="Lote"
                  value={lote}
                  onChange={(e) => setLote(e.target.value.toUpperCase())}
                  disabled={isReadOnly}
                  error={erroCampoMUI("lote")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              {/* 5. Geometria Regular */}
              <div className="form-row">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <span
                    style={{
                      marginBottom: 8,
                      fontWeight: 600,
                    }}
                  >
                    Geometria Regular*
                  </span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                      minHeight: 44,
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <input
                        type="radio"
                        name="geometriaRegular"
                        value="Não"
                        checked={geometriaRegular === "Não"}
                        onChange={(e) => {
                          if (isReadOnly) return;

                          setGeometriaRegular(e.target.value);
                          setMetrosFundo("");
                          setMetrosLadoDireito("");
                          setMetrosLadoEsquerdo("");
                        }}
                        aria-disabled={isReadOnly}
                        tabIndex={isReadOnly ? -1 : 0}
                        style={{
                          pointerEvents: isReadOnly ? "none" : "auto",
                          opacity: 1,
                        }}
                      />
                      Não
                    </label>

                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <input
                        type="radio"
                        name="geometriaRegular"
                        value="Sim"
                        checked={geometriaRegular === "Sim"}
                        onChange={(e) => {
                          if (isReadOnly) return;

                          setGeometriaRegular(e.target.value);
                        }}
                        aria-disabled={isReadOnly}
                        tabIndex={isReadOnly ? -1 : 0}
                        style={{
                          pointerEvents: isReadOnly ? "none" : "auto",
                          opacity: 1,
                        }}
                      />
                      Sim
                    </label>
                  </div>
                </div>
              </div>

              {/* 6. Metros de frente e medidas condicionais */}
              <div className="form-row">
                <TextField
                  label="Metros de Frente (m²)*"
                  value={metrosFrente}
                  inputMode="decimal"
                  onChange={handleMetragemChange("metrosFrente")}
                  onBlur={handleMetragemBlur("metrosFrente")}
                  disabled={isReadOnly}
                  error={erroCampoMUI("metrosFrente")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                {geometriaRegular === "Sim" && (
                  <>
                    <TextField
                      label="Metros de Fundo (m²)"
                      value={metrosFundo}
                      inputMode="decimal"
                      onChange={handleMetragemChange("metrosFundo")}
                      onBlur={handleMetragemBlur("metrosFundo")}
                      disabled={isReadOnly}
                      error={erroCampoMUI("metrosFundo")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Metros Lado Direito (m²)"
                      value={metrosLadoDireito}
                      inputMode="decimal"
                      onChange={handleMetragemChange("metrosLadoDireito")}
                      onBlur={handleMetragemBlur("metrosLadoDireito")}
                      disabled={isReadOnly}
                      error={erroCampoMUI("metrosLadoDireito")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Metros Lado Esquerdo (m²)"
                      value={metrosLadoEsquerdo}
                      inputMode="decimal"
                      onChange={handleMetragemChange("metrosLadoEsquerdo")}
                      onBlur={handleMetragemBlur("metrosLadoEsquerdo")}
                      disabled={isReadOnly}
                      error={erroCampoMUI("metrosLadoEsquerdo")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />
                  </>
                )}
              </div>

              {/* 7. Observações */}
              <div className="form-row">
                <TextField
                  label="Observações"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value.toUpperCase())}
                  disabled={isReadOnly}
                  error={erroCampoMUI("observacoes")}
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <div className="iptu-botoes">
                {modoVisualizacao ? (
                  <>
                    {podeEditar && (
                      <button type="button" className="iptu-btn-acao btn-cadastrar" onClick={(event) => handleEditar(event)}>
                        <FaEdit className="iptu-btn-icone" />
                        Editar
                      </button>
                    )}

                    <button type="button" className="iptu-btn-acao btn-acao" onClick={limparFormulario}>
                      <FaTimes className="iptu-btn-icone" />
                      Cancelar
                    </button>

                    {podeExcluir && (
                      <button type="button" className="iptu-btn-acao btn-excluir" onClick={() => handleDelete(visualizandoId)}>
                        <FaTrash className="iptu-btn-icone" />
                        Excluir
                      </button>
                    )}
                  </>
                ) : editandoId ? (
                  <>
                    <button type="submit" name="acaoFormulario" value="atualizar" className="iptu-btn-acao btn-cadastrar">
                      <FaEdit className="iptu-btn-icone" />
                      Atualizar
                    </button>

                    <button type="button" className="iptu-btn-acao btn-acao" onClick={limparFormulario}>
                      <FaTimes className="iptu-btn-icone" />
                      Cancelar
                    </button>

                    {podeExcluir && (
                      <button type="button" className="iptu-btn-acao btn-excluir" onClick={() => handleDelete(editandoId)}>
                        <FaTrash className="iptu-btn-icone" />
                        Excluir
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      type="submit"
                      name="acaoFormulario"
                      value="cadastrar"
                      className="iptu-btn-acao btn-cadastrar"
                      disabled={!dadosMatricula.matriculaId}
                    >
                      <FaSave className="iptu-btn-icone" />
                      Cadastrar
                    </button>

                    <button type="button" className="iptu-btn-acao btn-acao" onClick={limparFormulario}>
                      <FaTimes className="iptu-btn-icone" />
                      Cancelar
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        ) : (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default UnidadesLocaveis;
